import * as THREE from "three";
import { BURNT, FRIES_BURNT, type Contents, type Fries, type Patty, type Topping } from "../game/food";

// The food, real size (a patty is 11 cm across): patties that brown on the
// side that's down, the buns and toppings of a burger built up in a basket,
// and fries that go from pale to golden to burnt.

const RAW = new THREE.Color(0xd9686a);
const COOKED = new THREE.Color(0x6e3d22);
const CHARRED = new THREE.Color(0x1d130d);

/** A side's colour from how cooked it is: pink, then brown, then black. */
export function sideColor(level: number, out = new THREE.Color()): THREE.Color {
  if (level <= 1) return out.copy(RAW).lerp(COOKED, Math.max(0, level));
  return out.copy(COOKED).lerp(CHARRED, Math.min(1, (level - 1) / (BURNT - 1)));
}

const PALE = new THREE.Color(0xf3e3a2);
const GOLDEN = new THREE.Color(0xe3a12e);
const BURNT_FRY = new THREE.Color(0x553212);

export function friesColor(cook: number, out = new THREE.Color()): THREE.Color {
  if (cook <= 1) return out.copy(PALE).lerp(GOLDEN, Math.max(0, cook));
  return out.copy(GOLDEN).lerp(BURNT_FRY, Math.min(1, (cook - 1) / (FRIES_BURNT - 1)));
}

const PATTY_R = 0.055;
const PATTY_H = 0.018;
const pattyGeo = new THREE.CylinderGeometry(PATTY_R, PATTY_R * 0.97, PATTY_H, 20);

/** A patty whose bottom shows `sides[0]` and top `sides[1]`. */
export class PattyModel {
  readonly mesh: THREE.Mesh;
  private readonly side = new THREE.MeshStandardMaterial({ roughness: 0.8 });
  private readonly top = new THREE.MeshStandardMaterial({ roughness: 0.8 });
  private readonly bottom = new THREE.MeshStandardMaterial({ roughness: 0.8 });

  constructor() {
    // CylinderGeometry's groups: the side, the top cap, the bottom cap.
    this.mesh = new THREE.Mesh(pattyGeo, [this.side, this.top, this.bottom]);
    this.mesh.castShadow = true;
  }

  set(p: Patty): void {
    sideColor(p.sides[0], this.bottom.color);
    sideColor(p.sides[1], this.top.color);
    sideColor((p.sides[0] + p.sides[1]) / 2, this.side.color);
  }
}

const mat = (color: number, roughness = 0.7) => new THREE.MeshStandardMaterial({ color, roughness });
const BUN = mat(0xd99a4e, 0.6);
const BUN_CUT = mat(0xf2d7a0, 0.9);
const TOPPING_MATS: Record<Topping, THREE.MeshStandardMaterial> = {
  cheese: mat(0xf5c02a, 0.5),
  bacon: mat(0xa8442c, 0.5),
  lettuce: mat(0x6fbf45, 0.7),
  tomato: mat(0xd6362a, 0.4),
  onion: mat(0xf2ede4, 0.5),
  pickles: mat(0x7c9a35, 0.5),
};

const bunBottomGeo = new THREE.CylinderGeometry(0.06, 0.057, 0.02, 20);
const bunTopGeo = new THREE.SphereGeometry(0.062, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2);
const toppingGeo: Record<Topping, THREE.BufferGeometry> = {
  cheese: new THREE.BoxGeometry(0.1, 0.004, 0.1),
  bacon: new THREE.BoxGeometry(0.12, 0.005, 0.025),
  lettuce: new THREE.CylinderGeometry(0.068, 0.068, 0.006, 9),
  tomato: new THREE.CylinderGeometry(0.048, 0.048, 0.008, 16),
  onion: new THREE.TorusGeometry(0.036, 0.005, 6, 18),
  pickles: new THREE.CylinderGeometry(0.018, 0.018, 0.005, 10),
};
const TOPPING_STACK: readonly Topping[] = ["cheese", "bacon", "pickles", "onion", "tomato", "lettuce"];

/** One topping piece (or a few, for pickles), sitting with its bottom at y = 0. */
export function toppingPieces(t: Topping): THREE.Object3D {
  const g = new THREE.Group();
  const add = (x: number, z: number, ry = 0) => {
    const m = new THREE.Mesh(toppingGeo[t], TOPPING_MATS[t]);
    m.position.set(x, t === "onion" ? 0.005 : 0.003, z);
    if (t === "onion") m.rotation.x = Math.PI / 2;
    m.rotation.y = ry;
    g.add(m);
  };
  if (t === "pickles") [-0.025, 0, 0.025].forEach((x, i) => add(x, (i % 2) * 0.02 - 0.01));
  else if (t === "bacon") [-0.015, 0.015].forEach((z, i) => add(0, z, i * 0.2));
  else add(0, 0, t === "cheese" ? Math.PI / 4 : 0);
  return g;
}

/** A burger, built up from y = 0: bun, patties (cheese melting on the first), toppings, top bun. */
export function burgerModel(patties: Patty[], toppings: Topping[]): THREE.Group {
  const g = new THREE.Group();
  let y = 0;
  const stack = (o: THREE.Object3D, h: number) => {
    o.position.y += y;
    g.add(o);
    y += h;
  };
  const bottom = new THREE.Mesh(bunBottomGeo, [BUN, BUN_CUT, BUN]);
  bottom.position.y = 0.01;
  stack(bottom, 0.02);
  patties.forEach((p, i) => {
    const pm = new PattyModel();
    pm.set(p);
    pm.mesh.position.y = PATTY_H / 2;
    stack(pm.mesh, PATTY_H);
    if (i === 0 && toppings.includes("cheese")) stack(toppingPieces("cheese"), 0.005);
  });
  for (const t of TOPPING_STACK) if (t !== "cheese" && toppings.includes(t)) stack(toppingPieces(t), t === "onion" ? 0.01 : 0.007);
  if (!patties.length && toppings.includes("cheese")) stack(toppingPieces("cheese"), 0.005);
  if (patties.length) {
    const top = new THREE.Mesh(bunTopGeo, BUN);
    top.scale.y = 0.7;
    stack(top, 0.04);
  }
  g.traverse((o) => (o.castShadow = true));
  return g;
}

const fryGeo = new THREE.BoxGeometry(0.008, 0.008, 0.075);

/** A portion of fries, a loose pile about 10 cm across. */
export class FriesModel {
  readonly group = new THREE.Group();
  private readonly material = new THREE.MeshStandardMaterial({ roughness: 0.6 });

  constructor() {
    for (let i = 0; i < 16; i++) {
      const m = new THREE.Mesh(fryGeo, this.material);
      const a = i * 2.39;
      m.position.set(Math.cos(a) * 0.025 * ((i % 4) / 3), 0.006 + (i % 3) * 0.008, Math.sin(a) * 0.025 * ((i % 4) / 3));
      m.rotation.set((i % 5) * 0.2 - 0.4, a, (i % 3) * 0.25);
      m.castShadow = true;
      this.group.add(m);
    }
  }

  set(f: Fries): void {
    friesColor(f.cook, this.material.color);
  }
}

/** What's in a basket: the burger on one side, fries on the other. Rebuilt only when the contents change. */
export class ContentsModel {
  readonly group = new THREE.Group();
  private key = "";

  set(c: Contents): void {
    const key = JSON.stringify([c.patties.map((p) => p.sides.map((v) => Math.round(v * 10))), [...c.toppings].sort(), c.fries && Math.round(c.fries.cook * 10)]);
    if (key === this.key) return;
    this.key = key;
    this.group.clear();
    const burger = burgerModel(c.patties, c.toppings);
    burger.position.set(c.fries ? -0.04 : 0, 0.012, 0);
    this.group.add(burger);
    if (c.fries) {
      const fries = new FriesModel();
      fries.set(c.fries);
      fries.group.position.set(0.06, 0.012, 0);
      fries.group.scale.setScalar(0.8);
      this.group.add(fries.group);
    }
  }
}
